import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { motion } from 'motion/react';
import { api } from '../../../shared/api/api.js';
import type { Book } from '../../../shared/types/index.js';
import { useAuthStore } from '../../auth/store/useAuthStore.js';
import { useModalStore } from '../../../shared/store/useModalStore.js';
import { SeoHead, createWebSiteSchema, createOrganizationSchema, createFaqSchema } from '../../../shared/seo/index.js';
import { WhyUibnSection } from './WhyUibnSection.js';
import { BenefitsSection } from './BenefitsSection.js';
import { FaqSection } from './FaqSection.js';
import {
  LandingHeroSection,
  LandingSearchResults,
  StatsBannerSection,
  UserRolesGuideSection,
  FeatureHighlightsGrid,
  ShowcaseLandingSection,
  LandingCtaSection,
} from './sections/index.js';

interface LandingPageProps {
  onNavigateToDashboard: () => void;
  onOpenLogin: () => void;
  onOpenRegister: () => void;
  onSelectTab?: (tab: any) => void;
}

const ScrollReveal: React.FC<{ children: React.ReactNode; delay?: number }> = ({ children, delay = 0 }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-100px" }}
      transition={{ duration: 0.6, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.div>
  );
};

export const LandingPage: React.FC<LandingPageProps> = ({
  onNavigateToDashboard,
  onOpenLogin,
  onOpenRegister,
  onSelectTab,
}) => {
  const { user } = useAuthStore();
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState<Book[]>([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [searchPage, setSearchPage] = useState(1);
  const itemsPerPage = 10;
  const { setIsScannerOpen, setViewingBook: setSelectedBook } = useModalStore();

  const landingStructuredData = useMemo(() => {
    const origin = typeof window !== 'undefined' ? window.location.origin : 'https://uibn.id';
    return [
      createWebSiteSchema(origin),
      createOrganizationSchema(origin),
      createFaqSchema([
        {
          question: 'Apa itu UIBN dan apa perbedaannya dengan sistem penomoran konvensional?',
          answer: 'UIBN (Universal Indonesian Book Number) adalah sistem identifikasi buku digital modern di Indonesia dengan generator kode instan real-time ber-algoritma Modulo-10 checksum, barcode 1D, dan QR code 2D.',
        },
        {
          question: 'Siapa saja yang berhak mendaftarkan buku ke dalam sistem UIBN?',
          answer: 'Sistem UIBN terbuka untuk Penulis mandiri, akademisi, dosen perguruan tinggi, rumah penerbitan komersial, yayasan, lembaga penelitian, hingga komunitas literasi.',
        },
        {
          question: 'Bagaimana struktur kode UIBN dibentuk dan apakah ada validasi keasliannya?',
          answer: 'Nomor UIBN tersusun dalam pola terstandardisasi: UIBN-[KodeNegara]-[KodePenerbit]-[NomorUrutJudul]-[DigitChecksum] dengan verifikasi Modulo-10.',
        },
      ]),
    ];
  }, []);

  // Real-time FTS5 Autocomplete Query for Landing Search
  const [isLandingAutocompleteOpen, setIsLandingAutocompleteOpen] = useState(false);
  const { data: landingSuggestionsData, isLoading: isLandingSuggestionsLoading } = useQuery({
    queryKey: ['landing-suggestions-fts5', searchQuery],
    queryFn: async () => {
      const res = await api.getSearchSuggestions(searchQuery.trim());
      return res.suggestions || [];
    },
    enabled: searchQuery.trim().length >= 1,
    staleTime: 1000 * 20,
  });

  const landingSuggestions = landingSuggestionsData || [];

  // Fetch live stats teaser
  const { data: stats } = useQuery({
    queryKey: ['stats-public'],
    queryFn: () => api.getPublicStats(),
    refetchInterval: 15000,
  });

  const handleSearch = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!searchQuery.trim()) return;

    setIsSearching(true);
    setHasSearched(true);
    setSearchPage(1);
    try {
      const res = await api.searchBooks(searchQuery.trim());
      const approvedBooks = (res.books || []).filter((b) => b.status === 'disetujui');
      setSearchResults(approvedBooks);
      if (approvedBooks.length > 0) {
        api.recordScan(approvedBooks[0].uibn, 'manual_input', approvedBooks[0].id).catch(() => {});
      }
    } catch {
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const handleSelectSuggestion = async (item: any) => {
    setSearchQuery(item.title);
    setIsLandingAutocompleteOpen(false);
    try {
      const res = await api.getBookById(item.id);
      if (res.book) setSelectedBook(res.book);
    } catch {
      handleSearch();
    }
  };

  const handleCatalogExplore = () => {
    if (onSelectTab) {
      onSelectTab('showcase-directory');
      window.history.pushState({}, '', '/direktori');
    }
  };

  return (
    <div className="space-y-16 2xl:space-y-20 pb-16">
      <SeoHead
        title="UIBN – Universal Indonesian Book Number"
        description="Platform Universal Indonesian Book Number (UIBN). Sistem nomor unik buku resmi, direktori penulis & penerbit terverifikasi, e-sertifikat, dan pelestarian karya literasi Indonesia."
        ogType="website"
        keywords={['UIBN', 'Universal Indonesian Book Number', 'nomor unik buku', 'katalog buku indonesia', 'penerbit terverifikasi', 'penulis buku', 'registrasi buku nasional']}
        structuredData={landingStructuredData}
      />

      {/* Hero Section & Search Results */}
      <section className="pt-6 sm:pt-12">
        <LandingHeroSection
          searchQuery={searchQuery}
          isSearching={isSearching}
          isLandingAutocompleteOpen={isLandingAutocompleteOpen}
          landingSuggestions={landingSuggestions}
          isLandingSuggestionsLoading={isLandingSuggestionsLoading}
          onSearchQueryChange={setSearchQuery}
          onAutocompleteOpenChange={setIsLandingAutocompleteOpen}
          onSearchSubmit={handleSearch}
          onTriggerScanner={() => setIsScannerOpen(true)}
          onSelectSuggestion={handleSelectSuggestion}
          onExploreDirectory={handleCatalogExplore}
        />

        {hasSearched && (
          <LandingSearchResults
            searchResults={searchResults}
            searchQuery={searchQuery}
            searchPage={searchPage}
            itemsPerPage={itemsPerPage}
            onSelectBook={(b) => setSelectedBook(b)}
            onCloseResults={() => {
              setHasSearched(false);
              setSearchQuery('');
              setSearchPage(1);
            }}
            onPageChange={setSearchPage}
          />
        )}
      </section>

      {/* Real-time Statistics Banner */}
      <ScrollReveal>
        <StatsBannerSection stats={stats} />
      </ScrollReveal>

      {/* Mengapa UIBN Hadir (Latar Belakang & Visi) */}
      <ScrollReveal>
        <WhyUibnSection
          onOpenRegister={onOpenRegister}
          onExploreCatalog={handleCatalogExplore}
        />
      </ScrollReveal>

      {/* Benefit Mendaftarkan Buku di UIBN */}
      <ScrollReveal>
        <BenefitsSection
          onOpenRegister={onOpenRegister}
          onExploreCatalog={handleCatalogExplore}
        />
      </ScrollReveal>

      {/* Feature Showcase: Halaman Publik Penulis & Penerbit & Manfaatnya */}
      <ScrollReveal>
        <ShowcaseLandingSection
          onOpenRegister={onOpenRegister}
        />
      </ScrollReveal>

      {/* Quick Guide & Features for Authors and Publishers */}
      <ScrollReveal>
        <UserRolesGuideSection
          user={user}
          onOpenRegister={onOpenRegister}
          onNavigateToDashboard={onNavigateToDashboard}
          onExploreDirectory={handleCatalogExplore}
        />
      </ScrollReveal>

      {/* Feature Grid Highlights */}
      <ScrollReveal>
        <FeatureHighlightsGrid />
      </ScrollReveal>

      {/* Pertanyaan yang Sering Diajukan (FAQ) */}
      <ScrollReveal>
        <FaqSection />
      </ScrollReveal>

      {/* CTA Box */}
      <ScrollReveal>
        <LandingCtaSection
          onNavigateToDashboard={onNavigateToDashboard}
          onExploreDirectory={handleCatalogExplore}
        />
      </ScrollReveal>
    </div>
  );
};
